import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import AdminTable from "common/AdminTable";
import TruncatedMessage from "common/TruncatedMessage";
import { getRequest, deleteRequest, patchRequest } from "apis/ApiInstence";
import "./ProductList.css";

const AllProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10; // items per page

  const fetchProjects = async (pageNum = page) => {
    setLoading(true);
    try {
      const res = await getRequest(`projects?page=${pageNum}&limit=${limit}`);
      if (res.data.success) {
        setProjects(res.data.data);
        setTotalPages(res.data.totalPages || Math.ceil(res.data.total / limit));
        setPage(res.data.page);
      } else {
        toast.error(res.data.message || "Failed to fetch projects");
      }
    } catch (error) {
      toast.error("Error loading projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(1);
  }, []);

  // Fetch packages when editing and project_type changes
  useEffect(() => {
    if (currentProject && currentProject.project_type) {
      const fetchPackagesForType = async () => {
        setLoadingPackages(true);
        try {
          const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          const res = await getRequest(`package?property_type=${currentProject.project_type}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) {
            setPackages(res.data.data);
          } else {
            toast.error(res.data.message || 'Failed to load packages');
          }
        } catch (error) {
          console.error('Error fetching packages:', error);
          toast.error('Failed to load packages');
        } finally {
          setLoadingPackages(false);
        }
      };
      fetchPackagesForType();
    }
  }, [currentProject?.project_type]);

  const handleEditClick = (project) => {
    let imagesArray = [];
    try {
      imagesArray = JSON.parse(project.images);
    } catch (e) {
      imagesArray = [];
    }
    setExistingImages(imagesArray);
    setCurrentProject({
      ...project,
      is_active: project.is_active !== undefined ? project.is_active : true,
      project_area: project.project_area?.toString() || "",
      package_id: project.package?.id || "",
    });
    setImageFiles([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentProject(null);
    setImageFiles([]);
    setExistingImages([]);
    setPackages([]);
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (indexToRemove) => {
    setExistingImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSave = async () => {
    if (!currentProject) return;

    const formData = new FormData();
    formData.append("project_location", currentProject.project_location);
    formData.append("project_area", currentProject.project_area);
    formData.append("project_floor", currentProject.project_floor);
    formData.append("project_type", currentProject.project_type);
    formData.append("project_description", currentProject.project_description);
    formData.append("is_active", currentProject.is_active);
    formData.append("package_id", currentProject.package_id);

    if (existingImages.length > 0) {
      formData.append("existing_images", JSON.stringify(existingImages));
    } else {
      formData.append("existing_images", JSON.stringify([]));
    }

    imageFiles.forEach((file) => {
      formData.append("images", file);
    });

    try {
      const res = await patchRequest(`projects/${currentProject.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        toast.success("Project updated successfully");
        handleCloseModal();
        fetchProjects(page); // refresh current page
      } else {
        toast.error(res.data.message || "Update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating project");
    }
  };

  const handleRemove = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deleteRequest(`projects/${id}`);
          if (res.data.success) {
            toast.success("Project deleted successfully");
            // If current page becomes empty and not first page, go to previous page
            if (projects.length === 1 && page > 1) {
              fetchProjects(page - 1);
            } else {
              fetchProjects(page);
            }
          } else {
            toast.error(res.data.message || "Delete failed");
          }
        } catch (error) {
          toast.error("Error deleting project");
        }
      }
    });
  };

  const handlePrevPage = () => {
    if (page > 1) {
      fetchProjects(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      fetchProjects(page + 1);
    }
  };

  const tableHeaders = [
    { Header: "ID", accessor: "id" },
    {
      Header: "Images",
      accessor: "images",
      Cell: ({ row }) => {
        let imagesArray = [];
        try {
          imagesArray = JSON.parse(row.original.images);
        } catch (e) {
          imagesArray = [];
        }
        const firstImage = imagesArray[0];
        return firstImage ? (
          <img
            src={`${process.env.REACT_APP_BACKEND}${firstImage}`}
            alt="project"
            width={80}
            style={{ objectFit: "cover", borderRadius: "4px" }}
          />
        ) : (
          <span>No image</span>
        );
      },
    },
    { Header: "Location", accessor: "project_location" },
    { Header: "Area (sq ft)", accessor: "project_area" },
    { Header: "Floor", accessor: "project_floor" },
    { Header: "Type", accessor: "project_type" },
    {
      Header: "Package",
      accessor: "package",
      Cell: ({ row }) => row.original.package?.name || "—",
    },
    {
      Header: "Description",
      accessor: "project_description",
      Cell: ({ row }) => (
        <TruncatedMessage text={row.original.project_description} maxLength={40} />
      ),
    },
    {
      Header: "Active",
      accessor: "is_active",
      Cell: ({ row }) => (
        <span className={row.original.is_active ? "badge badge-success" : "badge badge-danger"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <>
          <button className="btn removebtn mx-1 my-1" onClick={() => handleRemove(row.original.id)}>
            Remove
          </button>
          <button className="btn editbtn mx-1 my-1" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      ),
    },
  ];

  if (loading) return <div className="text-center mt-5">Loading projects...</div>;

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="font-weight-bold">Project List</h4>
        </div>
        {projects.length > 0 ? (
          <>
            <AdminTable tableHeaders={tableHeaders} tableData={projects} />
            {/* Pagination Controls */}
            <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
              <div>
                Showing {projects.length} projects
              </div>
              <div>
                <button
                  className="btn btn-secondary mx-1"
                  onClick={handlePrevPage}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="mx-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary mx-1"
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center mt-4">No projects found.</div>
        )}
      </div >

      {/* Edit Modal (unchanged) */}
      {
        showModal && currentProject && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Project</h5>
                  <button type="button" className="close" onClick={handleCloseModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="projectStatus">Status</label>
                    <select
                      id="projectStatus"
                      className="form-control"
                      value={currentProject.is_active}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, is_active: e.target.value === "true" })
                      }
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  {/* Location */}
                  <div className="form-group">
                    <label htmlFor="projectLocation">Project Location</label>
                    <input
                      id="projectLocation"
                      type="text"
                      className="form-control"
                      value={currentProject.project_location}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, project_location: e.target.value })
                      }
                    />
                  </div>

                  {/* Area */}
                  <div className="form-group">
                    <label htmlFor="projectArea">Area (sq ft)</label>
                    <input
                      id="projectArea"
                      type="number"
                      className="form-control"
                      value={currentProject.project_area}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, project_area: e.target.value })
                      }
                    />
                  </div>

                  {/* Floor */}
                  <div className="form-group">
                    <label htmlFor="projectFloor">Floor</label>
                    <input
                      id="projectFloor"
                      type="text"
                      className="form-control"
                      value={currentProject.project_floor}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, project_floor: e.target.value })
                      }
                    />
                  </div>

                  {/* Type */}
                  <div className="form-group">
                    <label htmlFor="projectType">Project Type</label>
                    <select
                      id="projectType"
                      className="form-control"
                      value={currentProject.project_type}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, project_type: e.target.value, package_id: "" })
                      }
                    >
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Industrial">Industrial</option>
                      <option value="Townships">Townships</option>
                      <option value="Farmhouses">Farmhouses</option>
                    </select>
                  </div>

                  {/* Package selection */}
                  <div className="form-group">
                    <label htmlFor="packageId">Package</label>
                    <select
                      id="packageId"
                      className="form-control"
                      value={currentProject.package_id}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, package_id: e.target.value })
                      }
                      disabled={loadingPackages}
                    >
                      <option value="">-- Select a package --</option>
                      {packages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} (₹{pkg.price_sqft}/sq.ft)
                        </option>
                      ))}
                    </select>
                    {loadingPackages && <small className="text-muted">Loading packages...</small>}
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label htmlFor="projectDescription">Description</label>
                    <textarea
                      id="projectDescription"
                      className="form-control"
                      rows="3"
                      value={currentProject.project_description}
                      onChange={(e) =>
                        setCurrentProject({ ...currentProject, project_description: e.target.value })
                      }
                    />
                  </div>

                  {/* Existing Images */}
                  {existingImages.length > 0 && (
                    <div className="form-group">
                      <label htmlFor="existingImages">Current Images</label>
                      <div id="existingImages" className="d-flex flex-wrap">
                        {existingImages.map((img, idx) => (
                          <div key={idx} className="position-relative m-1">
                            <img
                              src={`${process.env.REACT_APP_BACKEND}${img}`}
                              alt="existing"
                              width={80}
                              height={80}
                              style={{ objectFit: "cover", borderRadius: "4px" }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute"
                              style={{ top: 0, right: 0 }}
                              onClick={() => removeExistingImage(idx)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload New Images */}
                  <div className="form-group">
                    <label htmlFor="newImages">Add New Images (multiple)</label>
                    <input
                      id="newImages"
                      type="file"
                      className="form-control-file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                    />
                    {imageFiles.length > 0 && (
                      <div className="mt-2">
                        <span className="font-weight-bold">New images to upload:</span>
                        <div className="d-flex flex-wrap mt-1">
                          {imageFiles.map((file, idx) => (
                            <div key={idx} className="position-relative m-1">
                              <img
                                src={URL.createObjectURL(file)}
                                alt="preview"
                                width={80}
                                height={80}
                                style={{ objectFit: "cover", borderRadius: "4px" }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                style={{ top: 0, right: 0 }}
                                onClick={() => removeNewImage(idx)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                  <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default AllProjects;